import { useState, useEffect } from "react";
import { cn } from "@/lib/utils";
import type { ServerProgressState } from "@/hooks/useSessionEvents";
import { SessionEvent, SessionEventType } from "@spectree/shared";
import { Clock, Layers, CheckCircle2, ListTodo, Lightbulb, ChevronRight } from "lucide-react";

export interface SessionSummaryPaneProps {
  progress: ServerProgressState;
  events: SessionEvent[];
  decisionsCount?: number;
  onDecisionsClick?: () => void;
  className?: string;
}

function formatDuration(seconds: number): string {
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);
  if (hrs > 0) return `${hrs}h ${mins.toString().padStart(2, "0")}m`;
  return `${mins}:${secs.toString().padStart(2, "0")}`;
}

export function SessionSummaryPane({ progress, events, decisionsCount = 0, onDecisionsClick, className }: SessionSummaryPaneProps) {
  const [sessionDuration, setSessionDuration] = useState(0);

  useEffect(() => {
    const start = events.find((e) => e.eventType === SessionEventType.SESSION_STARTED);
    if (!start) return;
    const startMs = new Date(start.timestamp).getTime();
    const tick = () => setSessionDuration(Math.floor((Date.now() - startMs) / 1000));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [events]);

  const phaseLabel =
    progress.currentPhase !== null
      ? `Phase ${progress.currentPhase}${progress.totalPhases ? ` / ${progress.totalPhases}` : ""}`
      : progress.lastCompletedPhase !== null
        ? `Phase ${progress.lastCompletedPhase} done`
        : "—";

  const stats = [
    {
      icon: Layers,
      label: "Phase",
      value: phaseLabel,
      color: "text-violet-400",
    },
    {
      icon: CheckCircle2,
      label: "Features",
      value: `${progress.completedFeatures} / ${progress.totalFeatures}`,
      color: "text-emerald-400",
    },
    {
      icon: ListTodo,
      label: "Tasks",
      value: `${progress.completedTasks} / ${progress.totalTasks}`,
      color: "text-sky-400",
    },
    {
      icon: Lightbulb,
      label: "Decisions",
      value: `${decisionsCount}`,
      color: "text-orange-400",
      onClick: onDecisionsClick,
      clickHint: Boolean(onDecisionsClick),
    },
    {
      icon: Clock,
      label: "Elapsed",
      value: formatDuration(sessionDuration),
      color: "text-amber-400",
    },
  ];

  return (
    <div className={cn("w-full", className)}>
      {/* Progress bar — thin, full width */}
      <div className="w-full h-1 bg-white/[0.04] rounded-full overflow-hidden mb-4">
        <div
          className="h-full bg-gradient-to-r from-emerald-500 to-emerald-400 transition-all duration-700 ease-out"
          style={{ width: `${progress.progressPercentage}%` }}
        />
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {stats.map((s) => {
          const Wrapper = s.onClick ? "button" : "div";
          return (
            <Wrapper
              key={s.label}
              onClick={s.onClick}
              className={cn(
                "flex items-center gap-2.5 rounded-lg border border-white/[0.06] bg-white/[0.02] px-3 py-2.5 text-left",
                s.onClick && "cursor-pointer hover:bg-white/[0.05] hover:border-white/[0.12] transition-colors"
              )}
            >
              <s.icon className={cn("h-4 w-4 shrink-0", s.color)} />
              <div className="min-w-0 flex-1">
                <p className="text-xs text-muted-foreground leading-none mb-0.5 uppercase tracking-wider">
                  {s.label}
                </p>
                <p className="text-sm font-medium tabular-nums truncate">{s.value}</p>
              </div>
              {s.clickHint && (
                <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground/50" />
              )}
            </Wrapper>
          );
        })}
      </div>

      {/* Percentage label */}
      <p className="text-xs text-muted-foreground mt-2.5 tabular-nums">
        {progress.progressPercentage}% complete
      </p>
    </div>
  );
}
